import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  PencilIcon, 
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
  CheckCircleIcon,
  PaperClipIcon,
  UserGroupIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email?: string;
}

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
  isLED?: boolean;
}

interface WorkOrderItem {
  id: number;
  structureId: number;
  Structure: Structure;
}

interface Client {
  id: number;
  name: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  Client: Client;
}

interface WorkOrder {
  id: number;
  workOrderNumber: string;
  orderId: number;
  type: 'placement' | 'videoReport';
  scheduledDate: string;
  deadline: string;
  status: 'pending' | 'inProgress' | 'completed' | 'cancelled';
  assignedToId: number | null;
  mediaFile?: string;
  comments?: string;
  createdAt: string;
  updatedAt: string;
  Order?: Order;
  AssignedTo?: User;
  WorkOrderItems?: WorkOrderItem[];
  createdById: number;
  CreatedBy?: User;
  lastUpdatedById?: number | null;
  UpdatedBy?: User;
}

const STATUS_OPTIONS: { [key: string]: string } = {
    pending: 'Ожидает исполнения',
    inProgress: 'В процессе',
    completed: 'Выполнен',
    cancelled: 'Отменен'
};

const TYPE_OPTIONS: { [key: string]: string } = {
    placement: 'Размещение рекламы',
    videoReport: 'Видеоотчет'
};

const WorkOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { can, user } = useAuth(); // Changed isAllowed to can
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  const canEditWorkOrder = can('workOrders.edit'); // Changed from isAllowed
  const canUpdateStatus = (
       can('workOrders.manage_status_advanced') || // Changed from isAllowed, new permission
       (can('workOrders.manage_status_assigned') && workOrder?.assignedToId === user?.id) // Changed from isAllowed, new permission
  );
  const canView = can('workOrders.view'); // Changed from isAllowed

  useEffect(() => {
    const fetchWorkOrderDetails = async () => {
       if (!canView) {
            setError('У вас нет прав для просмотра этого наряда.');
            setLoading(false);
            return;
        }
      setLoading(true);
      try {
        const response = await axios.get(`/work-orders/${id}`);
        setWorkOrder(response.data);
      } catch (err) {
        console.error('Error fetching work order details:', err);
        setError('Ошибка при загрузке данных наряда');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrderDetails();
  }, [id, canView]);
  
  const getAvailableStatuses = (): (keyof typeof STATUS_OPTIONS)[] => {
      if (!workOrder || !user) return [];
      const currentStatus = workOrder.status;
      const isAdminOrManager = can('workOrders.manage_status_advanced'); // Changed from isAllowed
      const isAssignedTechnician = can('workOrders.manage_status_assigned') && workOrder.assignedToId === user.id; // Changed from isAllowed

      if (isAdminOrManager) {
          if (currentStatus === 'completed' || currentStatus === 'cancelled') {
              return [currentStatus];
          }
          return Object.keys(STATUS_OPTIONS) as (keyof typeof STATUS_OPTIONS)[];
      } else if (isAssignedTechnician) {
          if (currentStatus === 'pending') return ['pending', 'inProgress'];
          if (currentStatus === 'inProgress') return ['inProgress', 'completed'];
          return [currentStatus];
      } 
      return [currentStatus];
  };

  const handleUpdateStatus = async (newStatus: keyof typeof STATUS_OPTIONS) => {
    if (!workOrder || !canUpdateStatus || newStatus === workOrder.status) return;
    
    const available = getAvailableStatuses();
    if (!available.includes(newStatus)) {
        alert('Недопустимое изменение статуса.');
        return;
    }

    setUpdatingStatus(true);
    try {
      await axios.patch(`/work-orders/${id}/status`, { status: newStatus });
      
      const response = await axios.get(`/work-orders/${id}`);
      setWorkOrder(response.data);
    } catch (err: any) {
      console.error('Error updating work order status:', err);
      alert(err.response?.data?.message || 'Ошибка при обновлении статуса наряда');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const formatDate = (dateString: string | undefined | null): string => {
      if (!dateString) return '-';
      try {
          const date = new Date(dateString);
          if(isNaN(date.getTime())) return 'Неверная дата';
          return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
      } catch(e) { return 'Ошибка даты'; }
  };
  
  const formatDateTime = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if(isNaN(date.getTime())) return 'Неверная дата';
        return new Intl.DateTimeFormat('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit' 
        }).format(date);
    } catch(e) { return 'Ошибка даты'; }
};
  
  const getUserName = (userParam?: User | null): string => {
    if (!userParam) return '-';
    return `${userParam.firstName} ${userParam.lastName}`;
  };

  const getAssignedTechnicianName = (): string => {
    if (workOrder?.assignedToId === null) return 'Не назначен';
    return getUserName(workOrder?.AssignedTo) ?? '-';
  };
  
  const getStatusText = (status: keyof typeof STATUS_OPTIONS | undefined): string => {
    return status ? (STATUS_OPTIONS[status] || String(status)) : '-';
  };
  
  const getStatusBadgeClass = (status: keyof typeof STATUS_OPTIONS | undefined): string => {
      if (!status) return 'bg-gray-100 text-gray-800';
      switch (status) {
          case 'pending': return 'bg-blue-100 text-blue-800';
          case 'inProgress': return 'bg-yellow-100 text-yellow-800';
          case 'completed': return 'bg-green-100 text-green-800';
          case 'cancelled': return 'bg-red-100 text-red-800';
          default: return 'bg-gray-100 text-gray-800';
      }
  };
  
  const getTypeText = (type: keyof typeof TYPE_OPTIONS | undefined): string => {
    return type ? (TYPE_OPTIONS[type] || String(type)) : '-';
  };
  
   const getTypeBadgeClass = (type: keyof typeof TYPE_OPTIONS | undefined): string => {
    if (!type) return 'bg-gray-100 text-gray-800';
    switch (type) {
      case 'placement': return 'bg-indigo-100 text-indigo-800';
      case 'videoReport': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isDeadlineCritical = (deadline: string | undefined | null, status: string | undefined): boolean => {
      if (!deadline || status === 'completed' || status === 'cancelled') return false;
      try {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const deadlineDate = new Date(deadline);
          if (isNaN(deadlineDate.getTime())) return false;
          deadlineDate.setHours(0, 0, 0, 0);
          return deadlineDate <= today;
      } catch(e) { return false; }
  };
  
  const renderVideo = () => {
    if (!workOrder?.mediaFile) return <p className="text-sm text-gray-500">Медиафайл не прикреплен.</p>;
    
    const url = workOrder.mediaFile;

    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1]) {
      const videoId = youtubeMatch[1];
      return (
        <div className="aspect-video relative">
          <iframe 
            className="absolute top-0 left-0 w-full h-full rounded-lg shadow"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      );
    }
    
    const videoExtensions = ['.mp4', '.webm', '.ogg'];
    if (videoExtensions.some(ext => url.toLowerCase().endsWith(ext))) {
      return (
        <video className="w-full rounded-lg shadow" controls>
          <source src={url} />
          Ваш браузер не поддерживает тег video.
           <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Скачать/открыть видео</a>
        </video>
      );
    }

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
     if (imageExtensions.some(ext => url.toLowerCase().endsWith(ext))) {
       return (
         <a href={url} target="_blank" rel="noopener noreferrer" title="Открыть изображение в новой вкладке">
            <img 
                src={url} 
                alt="Прикрепленное изображение" 
                className="max-w-full h-auto rounded-lg shadow hover:opacity-90 transition-opacity"
            />
         </a>
       );
     }
    
    return (
      <div className="p-3 bg-gray-100 rounded-lg border border-gray-200">
        <a 
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center text-sm"
        >
           <PaperClipIcon className="h-4 w-4 mr-1.5 text-gray-500"/>
          {url.split('/').pop() || 'Открыть прикрепленный файл/ссылку'}
        </a>
      </div>
    );
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">{error}</p>
        <Link to="/work-orders" className="mt-4 btn-secondary inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к списку нарядов
        </Link>
      </div>
    );
  }
  
  if (!workOrder) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-600">Наряд не найден.</p>
         <Link to="/work-orders" className="mt-4 btn-secondary inline-flex items-center">
           <ArrowLeftIcon className="h-5 w-5 mr-2" />
           Вернуться к списку нарядов
         </Link>
      </div>
    );
  }

  const availableStatuses = getAvailableStatuses();
  
  return (
    <div>
      <PageHeader 
        title={`Наряд №${workOrder.workOrderNumber || workOrder.id}`}
        backLink={workOrder.orderId ? `/orders/${workOrder.orderId}` : '/work-orders'}
        actions={
          canEditWorkOrder ? (
            <Link to={`/work-orders/edit/${id}`} className="btn-primary inline-flex items-center">
              <PencilIcon className="h-5 w-5 mr-2" />
              Редактировать
            </Link>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                    <InformationCircleIcon className="h-6 w-6 mr-2 text-primary"/>
                    Основная информация
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                     <div>
                        <dt className="text-gray-500">Номер Заказа:</dt>
                        <dd className="font-medium text-gray-800">
                            {workOrder.Order ? (
                                <Link to={`/orders/${workOrder.orderId}`} className="text-primary hover:underline">
                                    #{workOrder.Order.orderNumber}
                                </Link>
                            ) : `#${workOrder.orderId}`}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-gray-500">Клиент:</dt>
                        <dd className="font-medium text-gray-800">
                            {workOrder.Order?.Client ? (
                                <Link to={`/clients/${workOrder.Order.clientId}`} className="text-primary hover:underline">
                                    {workOrder.Order.Client.name}
                                </Link>
                            ) : '-'}
                        </dd>
                    </div>
                     <div>
                        <dt className="text-gray-500">Тип Наряда:</dt>
                        <dd className="font-medium">
                             <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeBadgeClass(workOrder.type)}`}>
                                {getTypeText(workOrder.type)}
                            </span>
                        </dd>
                    </div>
                     <div>
                         <dt className="text-gray-500">Статус:</dt>
                         <dd className="font-medium">
                             <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(workOrder.status)}`}>
                                 {getStatusText(workOrder.status)}
                             </span>
                         </dd>
                     </div>
                      <div>
                        <dt className="text-gray-500 flex items-center"><CalendarDaysIcon className="h-4 w-4 mr-1 text-gray-400"/>Плановая дата:</dt>
                        <dd className="font-medium text-gray-800">{formatDate(workOrder.scheduledDate)}</dd>
                    </div>
                    <div>
                        <dt className="text-gray-500 flex items-center"><ClockIcon className="h-4 w-4 mr-1 text-gray-400"/>Срок выполнения:</dt>
                         <dd className={`font-medium ${isDeadlineCritical(workOrder.deadline, workOrder.status) ? 'text-red-600' : 'text-gray-800'}`}>
                            {formatDate(workOrder.deadline)}
                         </dd>
                    </div>
                      <div>
                        <dt className="text-gray-500 flex items-center"><UserCircleIcon className="h-4 w-4 mr-1 text-gray-400"/>Исполнитель:</dt>
                        <dd className="font-medium text-gray-800">{getAssignedTechnicianName()}</dd>
                    </div>
                 </div>

                 {canUpdateStatus && (workOrder.status !== 'completed' && workOrder.status !== 'cancelled') && (
                     <div className="mt-6 pt-4 border-t border-gray-200">
                        <label htmlFor="status-update" className="block text-sm font-medium text-gray-700 mb-1">Изменить статус:</label>
                        <select 
                            id="status-update"
                            value={workOrder.status}
                            onChange={(e) => handleUpdateStatus(e.target.value as keyof typeof STATUS_OPTIONS)}
                            className="form-input-sm max-w-xs"
                            disabled={updatingStatus || availableStatuses.length <= 1}
                        >
                            {availableStatuses.map(statusKey => (
                                <option key={statusKey} value={statusKey}>
                                    {STATUS_OPTIONS[statusKey] || statusKey}
                                </option>
                            ))}
                        </select>
                        {updatingStatus && <span className="ml-2 text-xs text-gray-500">Обновление...</span>}
                     </div>
                 )}
            </div>

             <div className="bg-white rounded-lg shadow p-6">
                 <h2 className="text-xl font-bold mb-4">Конструкции в Наряде</h2>
                {workOrder.WorkOrderItems && workOrder.WorkOrderItems.length > 0 ? (
                     <ul className="divide-y divide-gray-200">
                         {workOrder.WorkOrderItems.map(item => (
                            <li key={item.id} className="py-3">
                                 <p className="text-sm font-medium text-gray-900">{item.Structure.code}</p>
                                 <p className="text-xs text-gray-600">{item.Structure.type} - {item.Structure.location}</p>
                             </li>
                         ))}
                     </ul>
                 ) : (
                     <p className="text-sm text-gray-500">Конструкции не указаны в этом наряде.</p>
                 )}
             </div>

              {(workOrder.comments) && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-bold mb-2">Комментарии</h2>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{workOrder.comments}</p>
                </div>
             )}

        </div>

        <div className="lg:col-span-1 space-y-6">
            {workOrder.type === 'videoReport' && (
                 <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-bold mb-4 flex items-center">
                       <PaperClipIcon className="h-6 w-6 mr-2 text-primary"/> 
                        Медиа Отчет
                    </h2>
                     {renderVideo()}
                 </div>
            )}

             <div className="bg-white rounded-lg shadow p-6">
                 <h2 className="text-xl font-bold mb-4 flex items-center">
                    <UserGroupIcon className="h-6 w-6 mr-2 text-gray-500"/>
                     История
                 </h2>
                 <div className="space-y-2 text-xs text-gray-600">
                     <p>Создан: <span className="font-medium">{getUserName(workOrder.CreatedBy)}</span> ({formatDateTime(workOrder.createdAt)})</p>
                     <p>Обновлен: <span className="font-medium">{getUserName(workOrder.UpdatedBy)}</span> ({formatDateTime(workOrder.updatedAt)})</p>
                 </div>
             </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderDetails;
